import React, { useState } from 'react';
import { submitJoinRequest } from '../services/api';

const REASON_MIN = 20;
const REASON_MAX = 500;

/**
 * Full-screen gate for authenticated non-members.
 * Shown when the backend returns NOT_A_MEMBER for /api/members/me.
 *
 * States:
 *   form          — request form (pre-filled name/email)
 *   submitted     — confirmation after successful submit
 *   already_pending — REQUEST_EXISTS from API
 */
export default function JoinRequestScreen({ user, onLogout }) {
  const [screenState, setScreenState] = useState('form');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || '';
  const email = user?.email || '';
  const remaining = REASON_MAX - reason.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < REASON_MIN) {
      setErrorMsg(`Please write at least ${REASON_MIN} characters.`);
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);

    try {
      await submitJoinRequest(reason.trim());
      setScreenState('submitted');
    } catch (err) {
      if (err.code === 'REQUEST_EXISTS') {
        setScreenState('already_pending');
      } else {
        setErrorMsg(err.message || 'Failed to submit request. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Wordmark */}
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <div className="flex items-center gap-[3px]" style={{ height: 18 }}>
            {[0.5, 0.75, 1, 0.75, 0.5].map((scale, i) => (
              <div
                key={i}
                className="rounded-full"
                style={{ width: 3, height: `${scale * 18}px`, background: 'var(--accent)', opacity: 0.85 }}
              />
            ))}
          </div>
          <span className="font-display font-semibold" style={{ fontSize: 20, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
            Larynx
          </span>
        </div>

        {screenState === 'form' && (
          <div className="card-paper p-8">
            <div className="text-center mb-6">
              <h2 className="text-xl font-extrabold" style={{ color: 'var(--text-primary)' }}>Ask to join.</h2>
              <p className="text-sm mt-1 font-medium" style={{ color: 'var(--text-muted)' }}>
                This is an invitation-only club. Tell us who you are and what brings you here.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name (pre-filled, read-only) */}
              <div>
                <label className="block text-sm font-bold mb-1" style={{ color: 'var(--text-secondary)' }}>Name</label>
                <input
                  type="text"
                  value={displayName}
                  readOnly
                  className="w-full px-3 py-2 border rounded-card bg-canvas text-sm"
                  style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }}
                />
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-bold mb-1" style={{ color: 'var(--text-secondary)' }}>Email</label>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="w-full px-3 py-2 border rounded-card bg-canvas text-sm"
                  style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }}
                />
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-bold mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Why do you want to speak? <span style={{ color: 'var(--color-danger)' }}>*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value.slice(0, REASON_MAX));
                    setErrorMsg(null);
                  }}
                  placeholder="What's driving you? What do you want to get better at?"
                  rows={4}
                  className="w-full px-3 py-2 border rounded-card text-sm resize-none focus:outline-none transition-colors"
                  style={{ borderColor: 'var(--border-subtle)' }}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs font-medium" style={{ color: reason.trim().length < REASON_MIN ? 'var(--text-faint)' : 'var(--color-success)' }}>
                    {reason.trim().length < REASON_MIN
                      ? `${REASON_MIN - reason.trim().length} more to go`
                      : 'Good to go'}
                  </span>
                  <span className="text-xs font-medium" style={{ color: remaining < 50 ? 'var(--color-danger)' : 'var(--text-faint)' }}>
                    {remaining} left
                  </span>
                </div>
              </div>

              {errorMsg && (
                <p className="text-sm font-medium px-3 py-2 rounded-card" style={{ color: 'var(--color-danger)', background: 'var(--color-danger-dim)' }}>
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting || reason.trim().length < REASON_MIN}
                className="w-full btn-bouncy font-extrabold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending…' : 'Send my request'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={onLogout}
                className="text-sm font-bold transition-colors hover:opacity-70"
                style={{ color: 'var(--text-faint)' }}
              >
                Sign out
              </button>
            </div>
          </div>
        )}

        {screenState === 'submitted' && (
          <div className="card-paper p-8 text-center">
            <h2 className="text-xl font-extrabold" style={{ color: 'var(--text-primary)' }}>Request sent.</h2>
            <p className="mt-2 text-sm font-medium leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              Someone will take a look. If it's a match, you'll hear back. Keep an eye on your inbox.
            </p>
            <button
              onClick={onLogout}
              className="mt-6 pill-tab font-bold hover:opacity-70" style={{ color: 'var(--text-faint)' }}
            >
              Sign out
            </button>
          </div>
        )}

        {screenState === 'already_pending' && (
          <div className="card-paper p-8 text-center">
            <h2 className="text-xl font-extrabold" style={{ color: 'var(--text-primary)' }}>Already in the queue.</h2>
            <p className="mt-2 text-sm font-medium leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              Your request is still being reviewed. Sit tight — you'll hear back.
            </p>
            <button
              onClick={onLogout}
              className="mt-6 pill-tab font-bold hover:opacity-70" style={{ color: 'var(--text-faint)' }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
